import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import type {
  DocsFolder,
  DocsPage,
} from "@siena-ai/design-system";
import type { SiteManifest, PublishedPage } from "../types";
import { toDocsSpace, toDocsFolder, toDocsPage } from "../adapters";
import { DocsSiteLayout } from "./DocsSiteLayout";

// Flattens folders+pages into a single ordered list that mirrors the sidebar:
// top-level folders by position → each folder's pages by position → each sub-folder
// (recursively). Only visible (published/public) pages are included, and folders
// without any visible descendants are skipped.
function buildOrderedPages(
  folders: DocsFolder[],
  pages: DocsPage[]
): DocsPage[] {
  const pagesByFolder = new Map<string, DocsPage[]>();
  for (const f of folders) {
    const fp = pages
      .filter(
        (p) =>
          p.folderIds.includes(f.id) &&
          p.status === "published" &&
          p.visibility === "public"
      )
      .sort((a, b) => a.position - b.position);
    if (fp.length > 0) pagesByFolder.set(f.id, fp);
  }

  const childrenOf = (parentId: string | null) =>
    folders
      .filter((f) => (f.parentFolderId ?? null) === parentId)
      .sort((a, b) => a.position - b.position);

  const hasVisibleContent = (folderId: string): boolean => {
    if (pagesByFolder.has(folderId)) return true;
    return childrenOf(folderId).some((sub) => hasVisibleContent(sub.id));
  };

  const ordered: DocsPage[] = [];
  const visit = (folderId: string) => {
    const fp = pagesByFolder.get(folderId);
    if (fp) ordered.push(...fp);
    for (const sub of childrenOf(folderId)) {
      if (hasVisibleContent(sub.id)) visit(sub.id);
    }
  };

  for (const top of childrenOf(null)) {
    if (hasVisibleContent(top.id)) visit(top.id);
  }
  return ordered;
}

interface SiteShellProps {
  manifest: SiteManifest;
  currentPage: PublishedPage | null;
  onNavigate: (slug: string | null, anchor?: string | null) => void;
  onNavigateToFolder?: (folderId: string) => void;
  children: React.ReactNode;
}

export const SiteShell: React.FC<SiteShellProps> = ({
  manifest,
  currentPage,
  onNavigate,
  onNavigateToFolder,
  children,
}) => {
  const space = useMemo(() => toDocsSpace(manifest), [manifest]);
  const folders = useMemo(
    () => manifest.folders.map(toDocsFolder),
    [manifest.folders]
  );
  const pages = useMemo(
    () => manifest.pages.map(toDocsPage),
    [manifest.pages]
  );

  const currentDocsPage = useMemo(() => {
    if (!currentPage) return null;
    const entry = manifest.pages.find((p) => p.slug === currentPage.slug);
    if (!entry) return null;
    return toDocsPage(entry);
  }, [currentPage, manifest.pages]);

  const currentFolderEntry = useMemo(() => {
    if (!currentPage) return null;
    const entry = manifest.pages.find((p) => p.slug === currentPage.slug);
    if (!entry) return null;
    return manifest.folders.find((f) => f.uuid === entry.folder_uuid) || null;
  }, [currentPage, manifest]);

  const currentFolder = currentFolderEntry
    ? toDocsFolder(currentFolderEntry)
    : null;

  const orderedPages = useMemo(
    () => buildOrderedPages(folders, pages),
    [folders, pages]
  );

  const currentIdx = currentDocsPage
    ? orderedPages.findIndex((p) => p.id === currentDocsPage.id)
    : -1;
  const prevPage = currentIdx > 0 ? orderedPages[currentIdx - 1] : null;
  const nextPage =
    currentIdx >= 0 && currentIdx < orderedPages.length - 1
      ? orderedPages[currentIdx + 1]
      : null;

  const seoTitle = currentPage
    ? `${currentPage.title} - ${manifest.space.seo.metaTitle || manifest.space.name}`
    : manifest.space.seo.metaTitle || manifest.space.name;

  const seoDescription = currentPage
    ? currentPage.description || manifest.space.seo.metaDescription
    : manifest.space.seo.metaDescription;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        {seoDescription && (
          <meta name="description" content={seoDescription} />
        )}
        {manifest.space.branding.faviconUrl && (
          <>
            <link rel="icon" href={manifest.space.branding.faviconUrl} sizes="any" type={manifest.space.branding.faviconUrl.endsWith(".svg") ? "image/svg+xml" : "image/png"} />
            <link rel="icon" href={manifest.space.branding.faviconUrl} sizes="32x32" />
            <link rel="apple-touch-icon" sizes="180x180" href={manifest.space.branding.faviconUrl} />
          </>
        )}
        {!manifest.space.seo.allowIndexing && (
          <meta name="robots" content="noindex, nofollow" />
        )}
      </Helmet>
      <DocsSiteLayout
        space={space}
        folders={folders}
        pages={pages}
        manifest={manifest}
        currentPage={currentDocsPage}
        currentFolder={currentFolder}
        prevPage={prevPage}
        nextPage={nextPage}
        pageHtmlContent={currentPage?.htmlContent}
        onPageClick={(page) => onNavigate(page.slug)}
        onSearchHitClick={(slug, anchor) => onNavigate(slug, anchor)}
        pageHref={(page) => `/${page.slug}`}
        onLogoClick={() => onNavigate(null)}
        onBreadcrumbFolderClick={(folderId) =>
          onNavigateToFolder?.(folderId)
        }
      >
        {children}
      </DocsSiteLayout>
    </>
  );
};
